import { http } from "./erp";

const ALLOWED_VARIABLES = ["name", "company", "phone", "link"];
const VARIABLE_EXPRESSION = /\{\{\s*(name|company|phone|link)\s*\}\}|\{(name|company|phone|link)\}/g;

/**
 * Accept international numbers only. Formatting is deliberately limited to
 * the optional leading plus sign, spaces, and hyphens.
 */
export function normalizeWhatsAppPhone(value) {
  const input = String(value ?? "");
  if (!/^\+?[0-9 -]+$/.test(input)) return null;
  const digits = input.replace(/[ -]/g, "").replace(/^\+/, "");
  return /^[1-9][0-9]{6,14}$/.test(digits) ? digits : null;
}

// The ERP's local mobile numbers use India (+91), as in its reminder defaults.
// Keep explicit international numbers unchanged and show the full number for review.
export function prefillDocumentWhatsAppPhone(value) {
  const input = String(value || "").trim();
  const digits = input.replace(/[ -]/g, "");
  return /^[6-9][0-9]{9}$/.test(digits) ? `+91${digits}` : input;
}

export function getWhatsAppTemplateVariables(message) {
  const found = [];
  const expression = new RegExp(VARIABLE_EXPRESSION.source, "g");
  let match;
  while ((match = expression.exec(String(message || "")))) {
    const key = match[1] || match[2];
    if (!found.includes(key)) found.push(key);
  }
  return found;
}

export function fillWhatsAppTemplate(message, values = {}) {
  return String(message || "").replace(new RegExp(VARIABLE_EXPRESSION.source, "g"), (_, doubleBraceKey, singleBraceKey) =>
    String(values[doubleBraceKey || singleBraceKey] ?? ""));
}

export function buildWhatsAppSendPayload({ phone, message, template, values = {} }) {
  const payload = {
    phone,
    message: fillWhatsAppTemplate(message, values),
    recipient_consent: true,
  };
  if (template) {
    const used = getWhatsAppTemplateVariables(template.message);
    payload.template_id = template.id;
    payload.values = Object.fromEntries(used.map((key) => [key, String(values[key] ?? "")]));
  }
  return payload;
}

function detailMessage(value) {
  if (typeof value === "string") return value;
  if (value && typeof value.msg === "string") return value.msg;
  if (value != null) return JSON.stringify(value);
  return "The request could not be completed.";
}

/** Convert FastAPI-style validation details into inline fields and a summary. */
export function extractWhatsAppErrors(error) {
  const detail = error?.response?.data?.detail;
  const fields = {};
  const messages = [];
  const add = (field, message) => {
    if (!messages.includes(message)) messages.push(message);
    if (field) {
      fields[field] = fields[field] ? `${fields[field]} ${message}` : message;
    }
  };

  if (Array.isArray(detail)) {
    detail.forEach((issue) => {
      const location = Array.isArray(issue?.loc) ? issue.loc : [];
      const field = [...location].reverse().find((part) => typeof part === "string" && part !== "body") || "";
      add(field, detailMessage(issue));
    });
  } else if (detail && typeof detail === "object" && !detail.msg) {
    Object.entries(detail).forEach(([field, value]) => {
      const entries = Array.isArray(value) ? value : [value];
      entries.forEach((entry) => add(field, detailMessage(entry)));
    });
  } else if (detail != null) {
    add("", detailMessage(detail));
  } else {
    add("", error?.response
      ? `Request failed (HTTP ${error.response.status}). Please try again.`
      : "Could not reach the WhatsApp service. Check your connection and try again.");
  }

  return { fields, messages };
}

export const whatsappApi = {
  getStatus: (config) => http.get("/whatsapp/status", config),
  connect: (payload, config) => http.post("/whatsapp/connect", payload, config),
  disconnect: (config) => http.post("/whatsapp/disconnect", {}, config),
  getTemplates: (config) => http.get("/whatsapp/templates", config),
  createTemplate: (payload, config) => http.post("/whatsapp/templates", payload, config),
  updateTemplate: (id, payload, config) => http.put(`/whatsapp/templates/${encodeURIComponent(id)}`, payload, config),
  deleteTemplate: (id, config) => http.delete(`/whatsapp/templates/${encodeURIComponent(id)}`, config),
  send: (payload, config) => http.post("/whatsapp/send", payload, config),
};

export { ALLOWED_VARIABLES };